/**
 * The keys stepper for ported product pages.
 *
 * This is the ONLY scripted control in the ported buy box, and the reason is worth stating,
 * because everything around it deliberately avoids script.
 *
 * Every other control the artboards draw as a card, pill or swatch is a native form control
 * wrapped in a <label>: the browser checks the radio, CSS `:has(:checked)` paints the chosen
 * state, and the picker's own listeners fire exactly as they would have. Nothing can desync,
 * because there is only one piece of state and the browser owns it.
 *
 * A stepper is the one shape with no native equivalent. The live control is a <select> whose
 * options carry their surcharge in `data-surcharge`, and product-configurator.js prices the line
 * from the SELECTED option. So the select is kept, hidden, and these buttons move it.
 *
 * Two details make that safe rather than clever:
 *
 *   - the buttons dispatch a real `change` event, so every listener on the select fires. Checked
 *     against assets/product-configurator.js on 2026-09-18: it attaches one `change` listener to
 *     the <product-configurator> element and never reads `event.isTrusted`, so a synthesised
 *     event is indistinguishable from a person using the select. If that ever changes, this
 *     bridge is where it breaks, and the keys surcharge would silently stop being applied.
 *   - the readout is rendered from the select's own selected option, never from a counter this
 *     file keeps. There is one source of truth and it is the select.
 *
 * The element is `<dc-stepper data-for="<id>">` holding two `[data-step]` buttons and one
 * `[data-stepper-value]` span.
 *
 * It drives EITHER a <select> (the keys field, whose options carry their own surcharge) or a
 * number <input> (a bulk-matrix row's quantity, which has no fixed set of values). Those are two
 * different shapes and the difference is real: a select steps through its options and clamps at
 * the ends, an input steps by one from its own min. Treating an input as a select read
 * `.options[undefined]` and threw on every bulk row.
 */
class DcStepper extends HTMLElement {
  connectedCallback() {
    // `data-for` names the control by id and wins, because a stepper can contain more than one
    // form element — a bulk-matrix row holds its keying select as well as its quantity input, and
    // taking the first one found would have stepped the wrong control.
    this.control = (this.dataset.for && document.getElementById(this.dataset.for))
      || this.querySelector('select, input');
    this.value = this.querySelector('[data-stepper-value]');
    this.buttons = Array.from(this.querySelectorAll('[data-step]'));

    if (!this.control || !this.buttons.length) return;
    this.isSelect = this.control.tagName === 'SELECT';

    this.onClick = this.onClick.bind(this);
    this.render = this.render.bind(this);

    this.buttons.forEach((b) => b.addEventListener('click', this.onClick));
    // The control can also change from elsewhere — the configurator enables and disables the keys
    // select when the key type changes, and bulk-matrix.js writes row quantities — so the readout
    // follows the control rather than the clicks.
    this.control.addEventListener('change', this.render);

    this.render();
  }

  disconnectedCallback() {
    this.buttons.forEach((b) => b.removeEventListener('click', this.onClick));
    if (this.control) this.control.removeEventListener('change', this.render);
  }

  /** Where the control is now, as a number: an option index, or the input's own value. */
  current() {
    return this.isSelect ? this.control.selectedIndex : Number(this.control.value) || 0;
  }

  /** The inclusive range the control may move in. */
  bounds() {
    if (this.isSelect) return { min: 0, max: this.control.options.length - 1 };
    const min = this.control.min === '' ? 0 : Number(this.control.min);
    const max = this.control.max === '' ? Infinity : Number(this.control.max);
    return { min, max };
  }

  onClick(event) {
    const step = Number(event.currentTarget.dataset.step) || 0;
    const { min, max } = this.bounds();
    const next = this.current() + step;

    // Clamped rather than wrapped. For the keys select the ends are the real minimum and maximum
    // number of keys the product sells, which differ per product (the aluminium padlock starts at
    // one key where the others start at two); for a quantity input the minimum is zero.
    if (next < min || next > max) return;

    if (this.isSelect) this.control.selectedIndex = next;
    else this.control.value = String(next);

    this.control.dispatchEvent(new Event('change', { bubbles: true }));
    this.render();
  }

  render() {
    const now = this.current();
    if (this.value) {
      const option = this.isSelect ? this.control.options[now] : null;
      this.value.textContent = this.isSelect ? (option ? option.value : '') : String(now);
    }

    // A disabled end of the range reads as a dead button, which is what the design's greyed
    // state is for; the control itself stays in the tab order.
    const { min, max } = this.bounds();
    this.buttons.forEach((b) => {
      const next = now + (Number(b.dataset.step) || 0);
      b.disabled = next < min || next > max;
    });
  }
}

if (!customElements.get('dc-stepper')) {
  customElements.define('dc-stepper', DcStepper);
}

/**
 * The line total in the add-to-cart button: unit price × quantity.
 *
 * The button is re-rendered by the section on every variant change, so the unit price and the
 * shop's money format are read off the span each time rather than cached — there is nothing here
 * to go stale. Delegated from the document for the same reason.
 *
 * Money formatting follows Shopify's `money_format` tokens: {{amount}}, {{amount_no_decimals}},
 * {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}.
 */
function dcFormatMoney(cents, format) {
  const n = cents / 100;
  const dot = (v, d) => v.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const comma = (v, d) => v.toFixed(d).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (format || '{{amount}}')
    .replace('{{amount_no_decimals_with_comma_separator}}', comma(n, 0))
    .replace('{{amount_with_comma_separator}}', comma(n, 2))
    .replace('{{amount_no_decimals}}', dot(n, 0))
    .replace('{{amount}}', dot(n, 2));
}

/**
 * (unit + options) × quantity, in cents. `options` is the configurator's surcharge in whole
 * currency units, as product-configurator.js's surcharge() returns it.
 */
function dcLineTotal(unitCents, options, qty) {
  return (unitCents + (Number(options) || 0) * 100) * Math.max(1, Number(qty) || 1);
}

/**
 * Recompute the total for one product <form>. The quantity input and the configurator both point
 * at it with `form=`, so it is the one thing the three triggers below share.
 *
 * Until 2026-10-01 this was unit × quantity only: 2 padlocks with CHF 4 of options read
 * "CHF 21.98" while the cart charged 29.98, and after a colour change the section's re-render put
 * back a one-unit price that nothing recomputed.
 */
function dcUpdateLineTotal(form) {
  // getAttribute, never `form.id`: the product form holds an <input name="id">, and a form's
  // named controls shadow its properties, so `form.id` is that input. The selectors below then
  // read "[object HTMLInputElement]", found no configurator, and the button left the options out
  // of its total on every padlock since 2026-10-01 (found on the stations, 2026-10-15).
  const formId = form?.getAttribute('id');
  if (!formId) return;
  const span = form.querySelector('[data-dc-line-total]');
  if (!span) return;
  const qtyInput = document.querySelector(`input[name="quantity"][form="${formId}"]`)
    || form.querySelector('input[name="quantity"]');
  const configurator = document.querySelector(`product-configurator[data-form="${formId}"]`);
  const options = typeof configurator?.surcharge === 'function' ? configurator.surcharge() : 0;
  const unit = Number(span.dataset.unitPrice) || 0;
  span.textContent = dcFormatMoney(dcLineTotal(unit, options, qtyInput?.value), span.dataset.moneyFormat);
  // The mobile sticky bar mirrors this label; tell it, rather than rely on listener order.
  document.dispatchEvent(new CustomEvent('dc:line-total', { detail: { form: formId } }));
}

for (const type of ['change', 'input']) {
  document.addEventListener(type, (event) => {
    const target = event.target;
    if (!target || !target.matches) return;
    if (target.matches('input[name="quantity"]')) {
      dcUpdateLineTotal(target.form);
      return;
    }
    // Any configurator control: key count, numbering on or off, key type.
    const configurator = target.closest('product-configurator');
    if (configurator) dcUpdateLineTotal(document.getElementById(configurator.dataset.form));
  });
}

// scripts.js replaces the button's innerHTML on a variant change and only then dispatches this, so
// recomputing here puts the quantity and options back into the fresh one-unit label.
document.addEventListener('product:variant-change', () => {
  document.querySelectorAll('form').forEach((form) => {
    if (form.querySelector('[data-dc-line-total]')) dcUpdateLineTotal(form);
  });
});

/**
 * The bulk matrix's keying pills.
 *
 * Same problem as the stepper and the same answer. The live control is a <select> per row whose
 * value becomes the "Key Type" cart property; the artboard draws two pills, KD and KA. A select
 * cannot be wrapped by two labels the way a radio pair can, so the pills write to it and
 * dispatch a real `change`.
 *
 * bulk-matrix.js listens for `change` on its host and branches on `[data-row-key-type]`, which is
 * how the keys field is enabled for a keyed-alike row. It does not read `event.isTrusted`
 * (checked 2026-09-18), so a synthesised event is indistinguishable from a person using the
 * select. If that ever changes, the keys surcharge on a bulk order stops being applied and
 * nothing else breaks — which is why this is written down.
 *
 * Delegated from the document rather than bound per row, because the matrix re-renders its rows.
 */
document.addEventListener('click', (event) => {
  const pick = event.target.closest('[data-row-key-pick]');
  if (!pick) return;

  const row = pick.closest('[data-row]');
  const select = row?.querySelector('[data-row-key-type]');
  if (!select) return;

  const index = Number(pick.dataset.rowKeyPick) || 0;
  if (select.selectedIndex === index) return;

  select.selectedIndex = index;
  select.dispatchEvent(new Event('change', { bubbles: true }));

  // The chosen pill is the design's own state, read off the select so the two cannot disagree.
  row.querySelectorAll('[data-row-key-pick]').forEach((b) => {
    b.classList.toggle('dc-on', Number(b.dataset.rowKeyPick) === select.selectedIndex);
  });
});
